import {parsePixels} from 'app/utils/dom';

export type OverflowSide = 'start' | 'end';

export const isOverflowSide = (value: unknown): value is OverflowSide => {
    return value === 'start' || value === 'end';
};

interface Range
{
    start: number;
    end: number;
}

const EDGE_TOLERANCE = 1;
const SMOOTH_SCROLL_TIMEOUT = 700;

const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Horizontal scroll strip helper shared by components whose items overflow into a scrollable row
 * (SegmentGroup, Tabs). Positions are computed in the scroller's content coordinates; the scroller's
 * `scroll-padding-inline` is treated as the width of the edge fade, so items under the fade count as not
 * fully visible. LTR documents only.
 */
export default class HorizontalOverflowScroller
{
    private scrollFrame: number | null = null;

    public constructor(
        private readonly scroller: HTMLElement,
        private readonly getItems: () => HTMLElement[],
        private readonly onScroll: () => void,
    )
    {
        this.handleScroll = this.handleScroll.bind(this);
    }

    public attach(): void {
        this.scroller.addEventListener('scroll', this.handleScroll, {passive: true});
    }

    public detach(): void {
        this.scroller.removeEventListener('scroll', this.handleScroll);
        if (this.scrollFrame !== null) {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = null;
        }
    }

    public hasOverflow(): boolean {
        return this.getMaxScroll() > EDGE_TOLERANCE;
    }

    public canScroll(side: OverflowSide): boolean {
        if (side === 'start') {
            return this.scroller.scrollLeft > EDGE_TOLERANCE;
        }
        return this.scroller.scrollLeft < this.getMaxScroll() - EDGE_TOLERANCE;
    }

    public getHiddenItems(side: OverflowSide): HTMLElement[] {
        const visible = this.getVisibleRange();
        return this.getItems().filter((item: HTMLElement): boolean => {
            const bounds = this.getBounds(item);
            if (side === 'start') {
                return bounds.start < visible.start - EDGE_TOLERANCE;
            }
            return bounds.end > visible.end + EDGE_TOLERANCE;
        });
    }

    public isItemVisible(item: HTMLElement): boolean {
        const bounds = this.getBounds(item);
        const visible = this.getVisibleRange();
        return bounds.start >= visible.start - EDGE_TOLERANCE && bounds.end <= visible.end + EDGE_TOLERANCE;
    }

    public scrollItemIntoView(item: HTMLElement, behavior: ScrollBehavior): Promise<void> {
        const bounds = this.getBounds(item);
        const visible = this.getVisibleRange();
        const insets = this.getInsets();
        if (bounds.start < visible.start - EDGE_TOLERANCE) {
            return this.scrollTo(bounds.start - insets.start, behavior);
        }
        if (bounds.end > visible.end + EDGE_TOLERANCE) {
            return this.scrollTo(bounds.end + insets.end - this.scroller.clientWidth, behavior);
        }
        return Promise.resolve();
    }

    /**
     * Scrolls by roughly one visible width so that the first fully visible item starts right after the fade.
     */
    public page(side: OverflowSide): Promise<void> {
        const items = this.getItems();
        const visible = this.getVisibleRange();
        const insets = this.getInsets();
        const current = this.scroller.scrollLeft;
        if (side === 'end') {
            const next = items.find((item: HTMLElement): boolean => this.getBounds(item).end > visible.end + EDGE_TOLERANCE);
            let target = next === undefined ? current : this.getBounds(next).start - insets.start;
            if (target <= current + EDGE_TOLERANCE) {
                target = visible.end - insets.start;
            }
            return this.scrollTo(target, 'smooth');
        }
        let previous: HTMLElement | null = null;
        for (const item of items) {
            if (this.getBounds(item).start < visible.start - EDGE_TOLERANCE) {
                previous = item;
            }
        }
        if (previous === null) {
            return this.scrollTo(0, 'smooth');
        }
        const raw = this.getBounds(previous).end + insets.end - this.scroller.clientWidth;
        const first = items.find((item: HTMLElement): boolean => this.getBounds(item).start >= raw + insets.start - EDGE_TOLERANCE);
        let target = first === undefined ? raw : this.getBounds(first).start - insets.start;
        if (target >= current - EDGE_TOLERANCE) {
            target = raw;
        }
        return this.scrollTo(target, 'smooth');
    }

    /**
     * A smooth scroll falls back to an instant one when the user prefers reduced motion.
     */
    private scrollTo(left: number, requestedBehavior: ScrollBehavior): Promise<void> {
        const target = Math.min(Math.max(left, 0), this.getMaxScroll());
        if (Math.abs(target - this.scroller.scrollLeft) < EDGE_TOLERANCE) {
            return Promise.resolve();
        }
        const behavior = requestedBehavior === 'smooth' && prefersReducedMotion() ? 'auto' : requestedBehavior;
        if (behavior !== 'smooth') {
            this.scroller.scrollTo({left: target, behavior});
            return Promise.resolve();
        }
        return new Promise<void>((resolve): void => {
            // `scrollend` is missing in older Safari, hence the timeout fallback.
            const done = (): void => {
                window.clearTimeout(timeout);
                this.scroller.removeEventListener('scrollend', done);
                resolve();
            };
            const timeout = window.setTimeout(done, SMOOTH_SCROLL_TIMEOUT);
            this.scroller.addEventListener('scrollend', done);
            this.scroller.scrollTo({left: target, behavior});
        });
    }

    private handleScroll(): void {
        if (this.scrollFrame !== null) {
            return;
        }
        this.scrollFrame = requestAnimationFrame((): void => {
            this.scrollFrame = null;
            this.onScroll();
        });
    }

    private getMaxScroll(): number {
        return this.scroller.scrollWidth - this.scroller.clientWidth;
    }

    private getInsets(): Range {
        const style = getComputedStyle(this.scroller);
        return {
            start: parsePixels(style.scrollPaddingLeft),
            end: parsePixels(style.scrollPaddingRight),
        };
    }

    private getVisibleRange(): Range {
        const insets = this.getInsets();
        const left = this.scroller.scrollLeft;
        return {
            start: left + (this.canScroll('start') ? insets.start : 0),
            end: left + this.scroller.clientWidth - (this.canScroll('end') ? insets.end : 0),
        };
    }

    private getBounds(item: HTMLElement): Range {
        const scrollerRect = this.scroller.getBoundingClientRect();
        const rect = item.getBoundingClientRect();
        const start = rect.left - scrollerRect.left - this.scroller.clientLeft + this.scroller.scrollLeft;
        return {start, end: start + rect.width};
    }
}
